'use client';

import { FloatingPortal } from '@floating-ui/react';
import { getClasses } from '@/_utils/classnames';
import styles from './Dialog.module.css';
import {
  ButtonGroup,
  Flex,
  Overlay,
  Separator,
  Heading,
  Button,
  Pill,
} from '@/_components';
import {
  Children,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
} from 'react';
import type { DialogProps } from './Dialog.types';

let dialogCounter: number = 0;

export const Dialog: FC<DialogProps> = ({
  children,
  className,
  disablePadding,
  headerButtons,
  isExtraWide,
  isFullScreen,
  isNarrow,
  isOptionalFullScreen,
  isOpen,
  isOverflowing,
  isPersistent,
  isWide,
  isTall,
  onClose,
  title,
  titleTag,
  allowScrollInFullScreenMode,
}) => {
  const [isOptionalFullScreenTriggered, setIsOptionalFullScreenTriggered] =
    useState<boolean>(false);

  const dialogId = useRef<number>(0);

  if (isOpen && !dialogId.current) {
    dialogId.current = ++dialogCounter;
  }

  const getTopMostDialogId = (): number => {
    const dialogs = document.querySelectorAll('[data-dialog-id]');

    return Math.max(
      ...Array.from(dialogs).map(
        (d) => Number(d.getAttribute('data-dialog-id')) || 0,
      ),
      0,
    );
  };

  const handleClose = useCallback(() => {
    if (!isPersistent) {
      const topMostId = getTopMostDialogId();

      if (dialogId.current === topMostId) {
        onClose?.();
      }
    }
  }, [isPersistent, onClose]);

  const toggleFullScreen = useCallback(() => {
    setIsOptionalFullScreenTriggered((previous) => !previous);
  }, []);

  const renderHeaderButtons = useCallback(
    () => (
      <ButtonGroup
        isSameLine={true}
        className={styles.dialog__header_buttons}
        isSmallGroup
      >
        {headerButtons}
        {isOptionalFullScreen && (
          <Button
            value={
              isOptionalFullScreenTriggered ? 'Exit fullscreen' : 'Fullscreen'
            }
            isIconOnly={true}
            size="small"
            iconLeft={
              isOptionalFullScreenTriggered ? 'corners-out' : 'arrows-out'
            }
            intent="ghost"
            withTooltip
            tooltipPlacement="bottom"
            onClick={toggleFullScreen}
          />
        )}
        {!isPersistent && (
          <>
            {headerButtons && (
              <Separator height={32} isLight={true} isVertical={true} />
            )}
            <Button
              value="Close"
              isIconOnly={true}
              size="small"
              iconLeft="x"
              intent="ghost"
              onClick={handleClose}
            />
          </>
        )}
      </ButtonGroup>
    ),
    [
      headerButtons,
      isOptionalFullScreen,
      isOptionalFullScreenTriggered,
      isPersistent,
      handleClose,
      toggleFullScreen,
    ],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    globalThis.document.addEventListener('keydown', handleKeyDown);
    return () =>
      globalThis.document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  if (!isOpen) return null;

  const dialogClasses: string = getClasses(
    styles.dialog,
    'elevation-lg',
    className,
    {
      [styles.dialogNarrow]: isNarrow,
      [styles.dialogWide]: isWide,
      [styles.dialogExtraWide]: isExtraWide,
      [styles.dialogNoPadding]: disablePadding,
      [styles.dialogFullScreen]: isFullScreen || isOptionalFullScreenTriggered,
      [styles.dialogOverflowing]: isOverflowing,
      [styles.dialogTall]: isTall,
      [styles.dialogFullScreenScrollable]:
        (isFullScreen || isOptionalFullScreenTriggered) &&
        allowScrollInFullScreenMode,
    },
  );

  const headerClasses = getClasses(styles.dialog__header, {
    [styles.dialog__header_complex]: !!titleTag,
  });

  return (
    <FloatingPortal>
      <Overlay onClose={handleClose}>
        <div
          className={dialogClasses}
          data-component="Dialog"
          data-dialog-id={dialogId.current}
          role="dialog"
          aria-label={title}
        >
          {title && (
            <Flex isColumn={true} className={headerClasses}>
              {titleTag ? (
                // Complex header with pill; accommodates long title text
                <Flex isColumn={true} gap={2}>
                  <Flex isBetween={true} isYCentered={true} gap={4}>
                    <Pill
                      intent="info"
                      label={titleTag}
                      isDark={false}
                      isRound={true}
                      isEllipsis={true}
                    />
                    {renderHeaderButtons()}
                  </Flex>
                  <Flex>
                    <Heading level={4}>{title}</Heading>
                  </Flex>
                </Flex>
              ) : (
                // Simple header
                <Flex isBetween={true} isYCentered={true} gap={2}>
                  <Heading level={4}>{title}</Heading>
                  {renderHeaderButtons()}
                </Flex>
              )}
            </Flex>
          )}
          {Children.map(children, (child) => {
            if (
              isValidElement<
                Pick<
                  DialogProps,
                  'isFullScreen' | 'isOverflowing' | 'disablePadding'
                >
              >(child)
            ) {
              return cloneElement(child, {
                isFullScreen,
                isOverflowing,
                disablePadding,
              });
            }
            return child;
          })}
        </div>
      </Overlay>
    </FloatingPortal>
  );
};
